import {
    Component,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    Output,
    EventEmitter,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { initializeEditor } from 'src/app/utils/angular-editor/editorConfig';
import { helper } from 'src/environments/helper';
import {
    ICerere,
} from '../../cereri.interface';
import {
    EditRequestAssigneePayload,
    EditRequestPriorityPayload,
    EditRequestStatusPayload,
} from './actualizare-cerere.interface';
import { CereriService } from '../../cereri.service';
import { NotificationService } from 'src/app/services/notification.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';
import { StatusService } from '../../../ghiseul-unic-settings/status/status.service';
import { PrioritiesService } from '../../../ghiseul-unic-settings/priorities/priorities.service';
import { DirectiiService } from '../../../../settings-sidenav/directii/directii.service';
import { CompartimenteService } from '../../../../settings-sidenav/compartimente/compartimente.service';
import { OperatoriService } from '../../../ghiseul-unic-settings/operatori/operatori.service';
// import { notDefaultValidator } from 'src/app/validators/not-default';

@Component({
    selector: 'app-actualizare-cerere',
    templateUrl: './actualizare-cerere.component.html',
    styleUrls: ['./actualizare-cerere.component.scss'],
})
export class ActualizareCerereComponent implements OnInit, OnChanges {
    @Input() cerere!: ICerere;
    @Output() cerereChanged: EventEmitter<ICerere> = new EventEmitter<ICerere>();

    passedRequestId!: number;
    type_directie_id!: number;

    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    editorConfig!: AngularEditorConfig;
    isLoading = false;
    selectedActionToggle = 'status';

    defaultChoiceParameter: { name: string; id: number } = {
        name: '-- Alegeți --',
        id: -1,
    };

    requestStatuses: any[] = [];
    requestPriorities: Generic[] = [];
    directiiActive: Generic[] = [];
    compartimenteActive: Generic[] = [];
    usersActive: Generic[] = [];

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    sendUpdateAssigneeForm!: FormGroup;
    sendUpdateStatusForm!: FormGroup;
    sendUpdatePriorityForm!: FormGroup;

    constructor(
        private commonService: CommonService,
        private fb: FormBuilder,
        private cereriService: CereriService,
        private notificationService: NotificationService,
        private statusService: StatusService,
        private prioritiesService: PrioritiesService,
        private directiiService: DirectiiService,
        private compartimenteService: CompartimenteService,
        private operatoriService: OperatoriService,
        public permissionsService: PermissionsService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        this.passedRequestId = this.cerere.id;
        this.type_directie_id = this.cerere.type_directie_id;
    }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.loadRequestPriorities();
        this.loadRequestStatuses();
        this.loadDirectiiActive();

        this.sendUpdateAssigneeForm = this.fb.group({
            directie_id: new FormControl(-1),
            compartiment_id: new FormControl(-1),
            user_id: new FormControl(-1, [Validators.required, Validators.min(0)]),
            content: new FormControl(''),
        });
        this.sendUpdateStatusForm = this.fb.group({
            current_status: new FormControl(-1, [Validators.required, Validators.min(0)]),
            message: new FormControl(''),
        });
        this.sendUpdatePriorityForm = this.fb.group({
            id_cerere_priority: new FormControl(-1, [Validators.required, Validators.min(0)]),
            message: new FormControl(''),
        });

        this.renderFieldsSearch();
        this.loadUsersActive();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    loadRequestPriorities() {
        this.prioritiesService
            .getPrioritiesActive()
            .pipe(map((results: any) => [this.defaultChoiceParameter, ...results]))
            .subscribe((data) => {
                this.requestPriorities = data;
            });
    }

    loadRequestStatuses() {
        this.statusService
            .getStatusesActive()
            .pipe(map((results: any) => [this.defaultChoiceParameter, ...results]))
            .subscribe((data) => {
                this.requestStatuses = data;
            });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.directiiService
            .getDirectiiActive()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.directiiActive = res;
                    this.filteredDirectii = res;
                },
                error: (err) => {
                    console.error('Error in get directii active:', err);
                }
            });
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.sendUpdateAssigneeForm.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.sendUpdateAssigneeForm.controls['compartiment_id'].patchValue(-1);
        this.sendUpdateAssigneeForm.controls['user_id'].patchValue(-1);

        this.loadUsersActive();

        if (directieId === -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
        } else {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.compartimenteService.getCompartimenteByDirectieId(directieId)
            : this.compartimenteService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        });
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.sendUpdateAssigneeForm.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.sendUpdateAssigneeForm.controls['user_id'].patchValue(-1);

        this.loadUsersActive();
    }

    loadUsersActive() {
        this.isLoading = true;

        const directie_id = this.sendUpdateAssigneeForm.controls['directie_id'].value;
        const compartiment_id = this.sendUpdateAssigneeForm.controls['compartiment_id'].value;

        this.operatoriService
            .getOperatoriFiltered({ directie_id, compartiment_id, active: true })
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            })
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.sendUpdateAssigneeForm.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onActionChange(event: MatButtonToggleChange) {
        this.selectedActionToggle = event.value;
    }

    submitUpdateAssigneeMessage() {
        this.isLoading = true;

        const formValues = this.sendUpdateAssigneeForm.value;

        const newRequestAssignee: EditRequestAssigneePayload = {
            ...formValues,
            id_cerere: this.cerere.id,
        };

        this.cereriService
            .updateRequestAssignee(this.cerere.id, newRequestAssignee)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    Swal.fire({
                        title: this.successTitle,
                        text: 'Escaladare actualizată cu succes!',
                        icon: 'success',
                        showCancelButton: false,
                        showConfirmButton: true,
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        allowOutsideClick: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            const directie_id = this.sendUpdateAssigneeForm.controls['directie_id'];
                            const compartiment_id = this.sendUpdateAssigneeForm.controls['compartiment_id'];
                            const user_id = this.sendUpdateAssigneeForm.controls['user_id'];
                            const content = this.sendUpdateAssigneeForm.controls['content'];
                            this.getRequestInfo();

                            directie_id.patchValue(-1);
                            compartiment_id.patchValue(-1);
                            user_id.patchValue(-1);
                            user_id.setErrors(null);
                            user_id.markAsUntouched();
                            content.patchValue('');

                            this.loadUsersActive();
                        }
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    submitUpdateStatusMessage() {
        this.isLoading = true;

        const formValues = this.sendUpdateStatusForm.value;

        const newRequestStatus: EditRequestStatusPayload = {
            ...formValues,
            id_cerere: this.cerere.id,
        };

        this.cereriService
            .updateRequestStatus(this.cerere.id, newRequestStatus)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    Swal.fire({
                        title: this.successTitle,
                        text: 'Status actualizat cu succes!',
                        icon: 'success',
                        showCancelButton: false,
                        showConfirmButton: true,
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        allowOutsideClick: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            const current_status = this.sendUpdateStatusForm.controls['current_status'];
                            const message = this.sendUpdateStatusForm.controls['message'];
                            this.getRequestInfo();

                            current_status.patchValue(-1);
                            current_status.setErrors(null);
                            current_status.markAsUntouched();
                            message.patchValue('');
                        }
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    submitUpdatePriorityMessage() {
        this.isLoading = true;

        const formValues = this.sendUpdatePriorityForm.value;

        const newRequestPriority: EditRequestPriorityPayload = {
            ...formValues,
            id_cerere: this.cerere.id,
        };

        this.cereriService
            .updateRequestPriority(this.cerere.id, newRequestPriority)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    Swal.fire({
                        title: this.successTitle,
                        text: 'Prioritate actualizată cu succes!',
                        icon: 'success',
                        showCancelButton: false,
                        showConfirmButton: true,
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        allowOutsideClick: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            const id_cerere_priority = this.sendUpdatePriorityForm.controls['id_cerere_priority'];
                            const message = this.sendUpdatePriorityForm.controls['message'];
                            this.getRequestInfo();

                            id_cerere_priority.patchValue(-1);
                            id_cerere_priority.setErrors(null);
                            id_cerere_priority.markAsUntouched();
                            message.patchValue('');
                        }
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    getRequestInfo() {
        this.isLoading = true;
        this.cereriService
            .getCerere(this.passedRequestId)
            .pipe(map((response: any) => response.data))
            .subscribe((requestData) => {
                this.isLoading = false;
                this.cerere = requestData;
                this.cerereChanged.emit(this.cerere);
            });
    }
}
